import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Sparkles, FileText, AlertTriangle, CheckCircle2, XCircle, ExternalLink, Trophy, Ban, Link2, ArrowUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import dayjs from "dayjs";

const STATUSES = [
  { v: "prospect", l: "Prospect" },
  { v: "drafting", l: "Drafting" },
  { v: "submitted", l: "Submitted" },
  { v: "awarded", l: "Awarded" },
  { v: "declined", l: "Declined" },
];

// Sprint A — Close the Loop. `submission_status` is intentionally SEPARATE from
// `status` (pipeline stage) — this field tracks the real-world outcome of the
// submission for institutional-memory purposes.
const SUBMISSION_STATUSES = [
  { v: "not_submitted", l: "Not submitted", cls: "bg-bone text-ink-soft border-line" },
  { v: "submitted",     l: "Submitted",     cls: "bg-forest/10 text-forest border-forest/40" },
  { v: "awarded",       l: "Awarded",       cls: "bg-terracotta text-bone border-terracotta" },
  { v: "declined",      l: "Declined",      cls: "bg-ink-soft/20 text-ink-soft border-line" },
  { v: "no_response",   l: "No response",   cls: "bg-ink-soft/10 text-ink-soft border-line" },
];

export default function Grants() {
  const [items, setItems] = useState([]);
  const [grantWriter, setGrantWriter] = useState(null);
  const [outputsByGrant, setOutputsByGrant] = useState({});
  const [assigning, setAssigning] = useState(null); // grant.id currently running
  const [viewingOutput, setViewingOutput] = useState(null);
  const [outcomeGrant, setOutcomeGrant] = useState(null);
  const [sortByDeadline, setSortByDeadline] = useState(false);

  const load = async () => {
    const { data } = await api.get("/grants");
    setItems(data);
    // Find the Senior Grant Writer (the only staff active in Sprint 2)
    try {
      const staff = await api.get("/staff", { params: { department: "funding" } });
      const sgw = staff.data.find(s => s.position === "Senior Grant Writer" && s.status !== "retired");
      setGrantWriter(sgw || null);
      if (sgw) {
        // Preload outputs so we can badge grants that already have analysis
        const outs = await api.get(`/staff/${sgw.id}/outputs`);
        const byGrant = {};
        outs.data.forEach(o => { byGrant[o.target_id] = byGrant[o.target_id] || []; byGrant[o.target_id].push(o); });
        setOutputsByGrant(byGrant);
      }
    } catch (err) {
      // If /staff or its child calls fail, the Assign button simply won't render
      // (grantWriter stays null). Log for diagnosability without disrupting the page.
      console.error("Grants: could not load Digital Staff context", err);
    }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => { await api.patch(`/grants/${id}`, { status }); load(); };
  const del = async (id) => { if (!window.confirm("Delete this grant?")) return; await api.delete(`/grants/${id}`); load(); };

  const assignToWriter = async (grant) => {
    if (!grantWriter) return toast.error("No active Grant Writer role configured.");
    if (!window.confirm(
      `Assign "${grant.name}" to ${grantWriter.name}?\n\n` +
      `The analysis will use ~2,500 tokens (~$0.03) and takes about 30 seconds.\n\n` +
      `Output enters your Pending Approval queue — nothing is submitted externally.`
    )) return;
    setAssigning(grant.id);
    try {
      const { data } = await api.post(`/staff/${grantWriter.id}/assign`, {
        target_type: "grant", target_id: grant.id,
      });
      toast.success(`Draft ready · fit score ${data.output.payload?.fit_score ?? "?"}/100 · pending your approval`);
      setViewingOutput(data.output);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Assignment failed");
    } finally { setAssigning(null); }
  };

  const COLOR = {
    prospect: "bg-forest/10 text-forest",
    drafting: "bg-terracotta/20 text-terracotta",
    submitted: "bg-forest/20 text-forest",
    awarded: "bg-terracotta text-bone",
    declined: "bg-ink-soft/20 text-ink-soft",
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Funding · Grant Center</div>
          <h1 className="font-serif text-4xl mt-1">Grant Pipeline</h1>
          {grantWriter && (
            <div className="text-xs text-ink-soft mt-2 flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5 text-terracotta" strokeWidth={1.5} />
              {grantWriter.name} available for draft-only assignments · human approval required on every output
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setSortByDeadline(v => !v)}
            data-testid="grants-sort-deadline"
            className={`border-line rounded-full text-xs ${sortByDeadline ? "bg-forest text-bone hover:bg-ink" : "bg-bone"}`}
            title="Sort grants by nearest deadline first"
          >
            <ArrowUpDown className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
            {sortByDeadline ? "Sorted by deadline" : "Sort by deadline"}
          </Button>
          {grantWriter && (
            <Link
              to={`/admin/staff/${grantWriter.id}/workspace`}
              data-testid="grants-open-workspace"
              className="h-9 px-4 rounded-full text-sm font-medium flex items-center gap-2 bg-forest text-bone hover:bg-ink transition-colors duration-150"
              title="Open the Senior Grant Writer's Workspace — Founder-facing office for intake, analysis, drafts, approvals"
            >
              <Sparkles className="h-3.5 w-3.5" strokeWidth={1.5} />
              Grant Writer Workspace
              <ExternalLink className="h-3 w-3" strokeWidth={1.5} />
            </Link>
          )}
          <NewGrant onCreated={load} />
        </div>
      </div>

      <div className="bg-admin-surface border border-line rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-bone/50 text-ink-soft">
            <tr>
              {["Grant","Funder","Amount","Deadline","Status","Outcome","AI Analysis",""].map(h => <th key={h} className="text-left px-4 py-3 font-medium uppercase tracking-widest text-xs">{h}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {(sortByDeadline
              ? [...items].sort((a, b) => {
                  // Empty deadlines sink to the bottom; nearest date first.
                  const da = a.deadline || "9999-12-31";
                  const db = b.deadline || "9999-12-31";
                  return da.localeCompare(db);
                })
              : items
            ).map(g => {
              const outs = outputsByGrant[g.id] || [];
              const pending = outs.filter(o => o.approval_status === "pending");
              const approved = outs.filter(o => o.approval_status === "approved");
              const subStatus = SUBMISSION_STATUSES.find(s => s.v === (g.submission_status || "not_submitted"));
              return (
                <tr key={g.id} data-testid={`grant-row-${g.id}`} className="hover:bg-bone/40">
                  <td className="px-4 py-3 font-medium text-ink">
                    <div className="flex items-center gap-2">
                      <span>{g.name}</span>
                      {g.linked_opportunity_id && (
                        <Badge className="bg-forest/10 text-forest border border-forest/40 text-[10px] uppercase tracking-widest inline-flex items-center gap-1" data-testid={`linked-badge-${g.id}`}>
                          <Link2 className="h-3 w-3" strokeWidth={1.5} />
                          Discovery
                        </Badge>
                      )}
                    </div>
                    {g.recommended_pathway_label && (
                      <div className="text-[11px] text-ink-soft italic mt-0.5">→ {g.recommended_pathway_label}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-ink-soft">{g.funder}</td>
                  <td className="px-4 py-3 text-ink">${(g.amount || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-ink-soft">{g.deadline ? dayjs(g.deadline).format("MMM D, YYYY") : "—"}</td>
                  <td className="px-4 py-3">
                    <Select value={g.status} onValueChange={(v) => updateStatus(g.id, v)}>
                      <SelectTrigger className={`h-8 w-36 ${COLOR[g.status] || ""}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setOutcomeGrant(g)}
                      data-testid={`outcome-btn-${g.id}`}
                      className="inline-flex items-center gap-1"
                      title="Record submission, award, feedback and lessons learned"
                    >
                      <Badge className={`border text-[10px] uppercase tracking-widest ${subStatus?.cls || "bg-bone text-ink-soft border-line"}`}>
                        {subStatus?.l || "—"}
                      </Badge>
                      {g.award_amount != null && g.submission_status === "awarded" && (
                        <span className="text-[11px] text-terracotta font-medium ml-1">
                          ${Number(g.award_amount).toLocaleString()}
                        </span>
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    {outs.length === 0 ? (
                      <span className="text-xs text-ink-soft/60">—</span>
                    ) : (
                      <button onClick={() => setViewingOutput(outs[0])} data-testid={`view-analysis-${g.id}`}
                        className="text-xs inline-flex items-center gap-1 text-terracotta hover:underline">
                        <FileText className="h-3 w-3" strokeWidth={1.5} />
                        {pending.length > 0 && <Badge className="bg-terracotta text-bone text-[10px]">Pending · {pending.length}</Badge>}
                        {approved.length > 0 && pending.length === 0 && <Badge className="bg-forest text-bone text-[10px]">Approved</Badge>}
                        {outs.length}
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      {grantWriter && (
                        <Button
                          onClick={() => assignToWriter(g)}
                          disabled={assigning === g.id}
                          data-testid={`assign-writer-${g.id}`}
                          className="bg-forest text-bone hover:bg-ink rounded-full h-8 px-3 text-xs"
                        >
                          <Sparkles className="h-3 w-3 mr-1.5" strokeWidth={1.5} />
                          {assigning === g.id ? "Analyzing…" : "Assign to Grant Writer"}
                        </Button>
                      )}
                      <button onClick={() => del(g.id)} className="text-ink-soft hover:text-terracotta" data-testid={`grant-delete-${g.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!items.length && <tr><td colSpan="8" className="text-center text-ink-soft py-10">No grants tracked yet. Add your first prospect.</td></tr>}
          </tbody>
        </table>
      </div>

      {viewingOutput && (
        <AnalysisViewer output={viewingOutput} onClose={() => setViewingOutput(null)} onDecided={() => { setViewingOutput(null); load(); }} />
      )}

      {outcomeGrant && (
        <OutcomeDialog
          grant={outcomeGrant}
          onClose={() => setOutcomeGrant(null)}
          onSaved={() => { setOutcomeGrant(null); load(); }}
        />
      )}
    </div>
  );
}

// Sprint A — Close the Loop. Outcome dialog: records what actually happened
// after submission for institutional-memory purposes. Zero LLM. Founder-only.
function OutcomeDialog({ grant, onClose, onSaved }) {
  const [f, setF] = useState({
    submission_status: grant.submission_status || "not_submitted",
    submitted_at: grant.submitted_at ? grant.submitted_at.slice(0, 10) : "",
    decided_at: grant.decided_at ? grant.decided_at.slice(0, 10) : "",
    award_amount: grant.award_amount ?? "",
    decision_notes: grant.decision_notes || "",
    reviewer_feedback: grant.reviewer_feedback || "",
    lessons_learned: grant.lessons_learned || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...f };
      // Coerce empty strings to null so backend timestamp auto-stamping fires.
      body.submitted_at = body.submitted_at || null;
      body.decided_at = body.decided_at || null;
      body.award_amount = body.award_amount === "" ? null : Number(body.award_amount);
      await api.patch(`/grants/${grant.id}`, body);
      toast.success("Outcome recorded.");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save outcome");
    } finally { setSaving(false); }
  };

  const s = f.submission_status;
  const isDecided = s === "awarded" || s === "declined" || s === "no_response";

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-bone" data-testid="outcome-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Record Grant Outcome</DialogTitle>
          <div className="text-xs text-ink-soft italic mt-1">{grant.name} · {grant.funder}</div>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="overline">Submission status</Label>
            <Select value={f.submission_status} onValueChange={v => setF({ ...f, submission_status: v })}>
              <SelectTrigger data-testid="outcome-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SUBMISSION_STATUSES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline">Submitted on</Label>
              <Input type="date" value={f.submitted_at} onChange={e => setF({ ...f, submitted_at: e.target.value })} data-testid="outcome-submitted-at" />
              <div className="text-[10px] text-ink-soft mt-1 italic">Auto-stamps if left blank.</div>
            </div>
            {isDecided && (
              <div>
                <Label className="overline">Decided on</Label>
                <Input type="date" value={f.decided_at} onChange={e => setF({ ...f, decided_at: e.target.value })} data-testid="outcome-decided-at" />
              </div>
            )}
          </div>
          {s === "awarded" && (
            <div>
              <Label className="overline flex items-center gap-1"><Trophy className="h-3 w-3" strokeWidth={1.5} /> Award amount (USD)</Label>
              <Input type="number" min="0" value={f.award_amount} onChange={e => setF({ ...f, award_amount: e.target.value })} data-testid="outcome-award-amount" />
            </div>
          )}
          {isDecided && (
            <div>
              <Label className="overline">Decision notes</Label>
              <Textarea rows={2} value={f.decision_notes} onChange={e => setF({ ...f, decision_notes: e.target.value })} data-testid="outcome-decision-notes" placeholder="Short summary of the funder's decision." />
            </div>
          )}
          {isDecided && (
            <div>
              <Label className="overline">Reviewer feedback (verbatim)</Label>
              <Textarea rows={3} value={f.reviewer_feedback} onChange={e => setF({ ...f, reviewer_feedback: e.target.value })} data-testid="outcome-reviewer-feedback" placeholder="Paste any feedback the funder shared." />
            </div>
          )}
          {isDecided && (
            <div>
              <Label className="overline">Lessons learned</Label>
              <Textarea rows={3} value={f.lessons_learned} onChange={e => setF({ ...f, lessons_learned: e.target.value })} data-testid="outcome-lessons-learned" placeholder="What would we do differently next time?" />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} data-testid="outcome-cancel" className="border-line">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="outcome-save" className="bg-forest text-bone hover:bg-ink rounded-full">
            {saving ? "Saving…" : "Save outcome"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AnalysisViewer({ output, onClose, onDecided }) {
  const [rejectNote, setRejectNote] = useState("");
  const [busy, setBusy] = useState(false);
  const p = output.payload || {};
  const tu = output.token_usage || {};

  const decide = async (action) => {
    setBusy(true);
    try {
      if (action === "approve") await api.post(`/staff/outputs/${output.id}/approve`);
      else await api.post(`/staff/outputs/${output.id}/reject`, { notes: rejectNote });
      toast.success(action === "approve" ? "Approved. Draft cleared for founder use." : "Rejected. Feedback recorded.");
      onDecided();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto bg-admin-surface border-line" data-testid="analysis-viewer">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl">{output.target_name}</DialogTitle>
          <div className="flex items-center gap-2 flex-wrap text-xs text-ink-soft">
            <Badge className={
              output.approval_status === "approved" ? "bg-forest text-bone" :
              output.approval_status === "rejected" ? "bg-line text-ink-soft" :
              "bg-terracotta text-bone"
            }>{output.approval_status}</Badge>
            <span>{output.model_used?.split("/")?.pop()}</span>
            <span>· ~{tu.estimated_total || 0} tokens</span>
            <span>· {new Date(output.created_at).toLocaleString()}</span>
          </div>
        </DialogHeader>

        <div className="rounded-md bg-terracotta/10 border border-terracotta/30 px-3 py-2 text-xs text-terracotta flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5" strokeWidth={1.5} />
          AI-generated draft — requires human review. Never externally submitted.
        </div>

        {p.fit_score !== undefined && (
          <div className="flex items-baseline gap-3 border-b border-line pb-3">
            <div className="font-serif text-5xl text-terracotta">{p.fit_score}</div>
            <div className="text-xs text-ink-soft uppercase tracking-widest">Mission fit / 100</div>
          </div>
        )}

        {p.fit_explanation && <VSection label="Mission fit">{p.fit_explanation}</VSection>}
        {p.opportunity_summary && <VSection label="Opportunity summary">{p.opportunity_summary}</VSection>}
        {p.draft_narrative && (
          <VSection label="Draft narrative">
            <div className="whitespace-pre-wrap bg-bone/50 border border-line rounded p-4 max-h-96 overflow-y-auto">{p.draft_narrative}</div>
          </VSection>
        )}
        {p.required_documents_checklist?.length > 0 && <VList label="Required documents" items={p.required_documents_checklist} />}
        {p.missing_information?.length > 0 && <VList label="Missing information (gather before submission)" items={p.missing_information} />}
        {p.eligibility_notes?.length > 0 && <VList label="Eligibility notes" items={p.eligibility_notes} />}
        {p.risks?.length > 0 && <VList label="Risks & concerns" items={p.risks} />}
        {p.recommended_next_step && <VSection label="Recommended next step"><em>{p.recommended_next_step}</em></VSection>}
        {p.error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800">Parse error: {p.error}</div>}

        {output.approval_status === "pending" && (
          <div className="pt-3 border-t border-line space-y-3">
            <div className="text-xs text-ink-soft">
              <strong>Your review</strong> — approving marks the draft as founder-cleared. Rejecting records feedback. Nothing is externally submitted either way.
            </div>
            <Textarea rows={2} placeholder="Optional feedback if rejecting…" value={rejectNote} onChange={e => setRejectNote(e.target.value)} data-testid="viewer-reject-notes" />
            <div className="flex gap-2 justify-end">
              <Button onClick={() => decide("reject")} disabled={busy} variant="outline" className="border-line" data-testid="viewer-reject">
                <XCircle className="h-4 w-4 mr-2" strokeWidth={1.5} /> Reject
              </Button>
              <Button onClick={() => decide("approve")} disabled={busy} data-testid="viewer-approve" className="bg-forest text-bone hover:bg-ink rounded-full">
                <CheckCircle2 className="h-4 w-4 mr-2" strokeWidth={1.5} /> Approve draft
              </Button>
            </div>
          </div>
        )}
        {output.approval_notes && (
          <div className="text-xs text-ink-soft italic border-t border-line pt-2">
            <strong>Feedback recorded:</strong> {output.approval_notes}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function VSection({ label, children }) {
  return (
    <div className="space-y-1.5">
      <div className="text-[10px] uppercase tracking-widest text-ink-soft">{label}</div>
      <div className="text-sm text-ink leading-relaxed">{children}</div>
    </div>
  );
}
function VList({ label, items }) {
  return (
    <div className="space-y-1.5">
      <div className="text-[10px] uppercase tracking-widest text-ink-soft">{label}</div>
      <ul className="text-sm text-ink space-y-1">
        {items.map((d, i) => <li key={i} className="flex items-start gap-2"><span className="text-terracotta mt-1">·</span>{d}</li>)}
      </ul>
    </div>
  );
}

function NewGrant({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", funder: "", amount: "", deadline: "", status: "prospect", notes: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/grants", f); toast.success("Grant added"); setOpen(false); onCreated(); setF({ name: "", funder: "", amount: "", deadline: "", status: "prospect", notes: "" }); }
    catch { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="pill pill-primary text-sm" data-testid="new-grant-btn"><Plus className="h-4 w-4" /> New Grant</button></DialogTrigger>
      <DialogContent className="bg-bone">
        <DialogHeader><DialogTitle className="font-serif text-2xl">New Grant</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label className="overline">Grant name</Label><Input required data-testid="ng-name" value={f.name} onChange={e=>setF({...f, name: e.target.value})} /></div>
          <div><Label className="overline">Funder</Label><Input data-testid="ng-funder" value={f.funder} onChange={e=>setF({...f, funder: e.target.value})} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="overline">Amount (USD)</Label><Input type="number" min="0" data-testid="ng-amount" value={f.amount} onChange={e=>setF({...f, amount: e.target.value})} /></div>
            <div><Label className="overline">Deadline</Label><Input type="date" data-testid="ng-deadline" value={f.deadline} onChange={e=>setF({...f, deadline: e.target.value})} /></div>
          </div>
          <div><Label className="overline">Status</Label>
            <Select value={f.status} onValueChange={v => setF({...f, status: v})}>
              <SelectTrigger data-testid="ng-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="overline">Notes</Label><Textarea rows={3} data-testid="ng-notes" value={f.notes} onChange={e=>setF({...f, notes: e.target.value})} /></div>
          <button type="submit" data-testid="ng-submit" className="pill pill-primary">Save</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
